import { html, css, LitElement, nothing, type PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import type { ProfileCircleColor } from 'zaptic-types'
import { getInitials } from '../lib/names'
import { profileCircleColorAttributeSchema } from './helpers/yup'

/**
 * A circular profile image or initials with a coloured background.
 */
@customElement('zaptic-profile-circle')
export class ProfileCircle extends LitElement {
    static styles = css`
        /* N.B. can't use nested CSS here because this is tested with puppeteer, and we're currently
                stuck on an ancient version, which bundles an ancient browser :-(
                See https://zaptic.atlassian.net/browse/ZAP-3692
        */
        :host {
            padding-right: var(--ProfileCircle-paddingRight);
        }

        .profile-image {
            display: none;
            height: var(--ProfileCircle-size);
            width: var(--ProfileCircle-size);
            border-radius: 50%;
            vertical-align: middle;
        }

        .profile-image.image-loaded {
            display: block;
        }

        zaptic-labelled-circle {
            --LabelledCircle-backgroundColor: var(--ProfileCircle-backgroundColor);
            --LabelledCircle-textColor: white;
            --LabelledCircle-size: var(--ProfileCircle-size);
            filter: brightness(var(--ProfileCircle-brightness));
        }

        :host([color='purple']) zaptic-labelled-circle {
            --LabelledCircle-backgroundColor: var(--purple);
        }
        :host([color='blue']) zaptic-labelled-circle {
            --LabelledCircle-backgroundColor: var(--blue-500);
        }
        :host([color='green']) zaptic-labelled-circle {
            --LabelledCircle-backgroundColor: var(--green);
        }
        :host([color='pink']) zaptic-labelled-circle {
            --LabelledCircle-backgroundColor: var(--pink);
        }
        :host([color='orange']) zaptic-labelled-circle {
            --LabelledCircle-backgroundColor: var(--orange);
        }
        :host([color='red']) zaptic-labelled-circle {
            --LabelledCircle-backgroundColor: var(--red);
        }
    `

    /** A cache of URLs which have previously loaded successfully.
     *
     * If an image has previously loaded sucessfully, then we assume
     * that the browser has cached that image, and so we can avoid
     * initially showing the initials and then almost-immediately switching
     * to the image (which can cause a noticeable flicker).
     */
    private static previouslyLoadedImageUrls = new Set<string>()

    @property({ reflect: true })
    name: string = ''

    @state()
    private _initials: string = ''

    // N.B. using 'color' as a property name is unfortunate because it clashes with the 'color' CSS property
    //      which confuses the intellisense on the react wrapper.
    //      So the property is named `profileColor`, but the attribute is still `color`.
    @property({
        attribute: 'color',
        reflect: true,
        converter: (value) => profileCircleColorAttributeSchema.validateSync(value),
    })
    profileColor: ProfileCircleColor

    @property({ attribute: 'profile-image-url' })
    profileImageUrl: string

    @state()
    profileImageLoaded: boolean = false

    willUpdate(changedProperties: PropertyValues<ProfileCircle>) {
        if (changedProperties.has('name')) {
            this._initials = getInitials(this.name ?? '')
        }
        if (changedProperties.has('profileImageUrl')) {
            this.profileImageLoaded = ProfileCircle.previouslyLoadedImageUrls.has(this.profileImageUrl)
        }
    }

    render() {
        return html`${this.renderProfileImage()}${this.profileImageLoaded ? nothing : this.renderInitialsInCircle()}`
    }

    private renderProfileImage() {
        if (!this.profileImageUrl) return nothing

        return html`<img
            class="${`profile-image ${this.profileImageLoaded ? 'image-loaded' : ''}`}"
            src="${this.profileImageUrl}"
            alt="${this.name}"
            @load="${() => {
                this.profileImageLoaded = true
                ProfileCircle.previouslyLoadedImageUrls.add(this.profileImageUrl)
            }}"
            @error="${() => {
                this.profileImageLoaded = false
                ProfileCircle.previouslyLoadedImageUrls.delete(this.profileImageUrl)
            }}"
        />`
    }

    private renderInitialsInCircle() {
        const label = this._initials
        return html`
            <zaptic-labelled-circle
                font-size="${calculateFontSizeToFit(label)}"
                label="${label}"
            ></zaptic-labelled-circle>
        `
    }
}

/**
 * Exponential decay to calculate a shrinking size between 10px and 50px
 */
function calculateFontSizeToFit(label: string) {
    return 10 + 40 * Math.pow(0.8, label.length - 1)
}

declare global {
    interface HTMLElementTagNameMap {
        'zaptic-profile-circle': ProfileCircle
    }
}
